import { create } from 'zustand';

interface PlanStep {
  id: number;
  label: string;
  debut: number;
  fin: number;
}

interface PlanningState {
  planSteps: PlanStep[];
  planAssign: Record<string, number[]>;
  popOverrides: Record<number, number>;
  costOverrides: Record<string, Record<number, number | null>>;
  nextId: number;

  addStep: () => void;
  removeStep: (id: number) => void;
  updateStep: (id: number, field: keyof Omit<PlanStep, 'id'>, value: string | number) => void;
  toggleAssign: (item: string, stepId: number) => void;
  setPopOverride: (year: number, pct: number) => void;
  clearPopOverride: (year: number) => void;
  setCostOverride: (item: string, stepId: number, value: number | null) => void;
  clearAllCostOverrides: () => void;
  resetPlanning: () => void;
  hydrate: (data: Partial<Pick<PlanningState, 'planSteps' | 'planAssign' | 'popOverrides' | 'costOverrides' | 'nextId'>>) => void;
}

const DEFAULT_STEPS: PlanStep[] = [
  { id: 1, label: 'Étape 1', debut: 2026, fin: 2030 },
  { id: 2, label: 'Étape 2', debut: 2031, fin: 2035 },
  { id: 3, label: 'Étape 3', debut: 2036, fin: 2040 },
];

const DEFAULT_ASSIGN: Record<string, number[]> = {
  infra: [1],
  ep: [1, 2],
  ecoles: [2],
  creches: [2],
  co: [3],
  sport: [3],
  culturel: [3],
  zi: [],
};

export const usePlanningStore = create<PlanningState>((set, get) => ({
  planSteps: [...DEFAULT_STEPS],
  planAssign: { ...DEFAULT_ASSIGN },
  popOverrides: {},
  costOverrides: {},
  nextId: 4,

  addStep: () => {
    const { planSteps, nextId } = get();
    if (planSteps.length >= 10) return;
    const last = planSteps[planSteps.length - 1];
    const debut = last ? last.fin + 1 : 2026;
    set({
      planSteps: [...planSteps, { id: nextId, label: `Étape ${planSteps.length + 1}`, debut, fin: debut + 3 }],
      nextId: nextId + 1,
    });
  },

  removeStep: (id) =>
    set((state) => ({
      planSteps: state.planSteps.filter((s) => s.id !== id),
      planAssign: Object.fromEntries(
        Object.entries(state.planAssign).map(([k, v]) => [k, v.filter((x) => x !== id)])
      ),
    })),

  updateStep: (id, field, value) =>
    set((state) => ({
      planSteps: state.planSteps.map((s) =>
        s.id === id ? { ...s, [field]: value } : s
      ),
    })),

  toggleAssign: (item, stepId) =>
    set((state) => ({
      planAssign: {
        ...state.planAssign,
        [item]: state.planAssign[item]?.includes(stepId)
          ? state.planAssign[item].filter((x) => x !== stepId)
          : [...(state.planAssign[item] || []), stepId],
      },
    })),

  setPopOverride: (year, pct) =>
    set((state) => ({
      popOverrides: { ...state.popOverrides, [year]: pct },
    })),

  clearPopOverride: (year) =>
    set((state) => {
      const { [year]: _, ...rest } = state.popOverrides;
      return { popOverrides: rest };
    }),

  setCostOverride: (item, stepId, value) =>
    set((state) => ({
      costOverrides: {
        ...state.costOverrides,
        [item]: { ...(state.costOverrides[item] || {}), [stepId]: value },
      },
    })),

  clearAllCostOverrides: () => set({ costOverrides: {} }),

  resetPlanning: () =>
    set({
      planSteps: [...DEFAULT_STEPS],
      planAssign: { ...DEFAULT_ASSIGN },
      popOverrides: {},
      costOverrides: {},
      nextId: 4,
    }),
  hydrate: (data) => set(data),
}));
